<script setup>
/* global axios */
// Atribuição por inatividade (por caixa): atribui conversas paradas há N minutos
// a um agente fixo ou distribui (round-robin) entre os agentes da caixa.
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inbox: { type: Object, required: true },
});
const { t } = useI18n();
const route = useRoute();
const accountId = route.params.accountId;

const loading = ref(true);
const saving = ref(false);
const agents = ref([]);
const cfg = reactive({
  enabled: false,
  inactivity_minutes: 30,
  mode: 'agent',
  agent_id: null,
  only_unassigned: true,
});

const base = computed(
  () =>
    `/api/v1/accounts/${accountId}/kanban/inbox_inactivity_rules/${props.inbox.id}`
);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(base.value);
    Object.assign(cfg, data.data || {});
    agents.value = data.agents || [];
  } catch (e) {
    /* sem regra ainda */
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  try {
    await axios.put(base.value, {
      enabled: cfg.enabled,
      inactivity_minutes: Number(cfg.inactivity_minutes) || 30,
      mode: cfg.mode,
      agent_id: cfg.mode === 'agent' ? cfg.agent_id : null,
      only_unassigned: cfg.only_unassigned,
    });
    useAlert(t('INBOX_MGMT.INACTIVITY_ASSIGN.SAVED'));
  } catch (e) {
    useAlert(t('INBOX_MGMT.INACTIVITY_ASSIGN.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

watch(() => props.inbox.id, load);
onMounted(load);
</script>

<template>
  <div
    class="p-5 mt-4 border rounded-xl border-n-weak bg-n-solid-1"
    :class="{ 'opacity-60': loading }"
  >
    <div class="flex items-start justify-between gap-3 mb-1">
      <div>
        <h4 class="text-base font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.INACTIVITY_ASSIGN.TITLE') }}
        </h4>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.INACTIVITY_ASSIGN.DESC') }}
        </p>
      </div>
      <label class="relative inline-flex items-center cursor-pointer shrink-0">
        <input v-model="cfg.enabled" type="checkbox" class="sr-only peer" />
        <span
          class="w-9 h-5 rounded-full bg-n-alpha-2 peer-checked:bg-n-brand transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"
        />
      </label>
    </div>

    <div v-if="cfg.enabled" class="pt-4 mt-3 border-t border-n-weak flex flex-col gap-4">
      <label class="block">
        <span class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('INBOX_MGMT.INACTIVITY_ASSIGN.MINUTES')
        }}</span>
        <input
          v-model="cfg.inactivity_minutes"
          type="number"
          min="1"
          class="w-40 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        />
        <span class="block mt-1 text-[0.6875rem] text-n-slate-10">{{
          t('INBOX_MGMT.INACTIVITY_ASSIGN.MINUTES_HINT')
        }}</span>
      </label>

      <div>
        <span class="block mb-1.5 text-sm font-medium text-n-slate-12">{{
          t('INBOX_MGMT.INACTIVITY_ASSIGN.TARGET')
        }}</span>
        <div class="flex flex-col gap-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input v-model="cfg.mode" type="radio" value="agent" />
            <span class="text-sm text-n-slate-12">{{
              t('INBOX_MGMT.INACTIVITY_ASSIGN.MODE_AGENT')
            }}</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer">
            <input v-model="cfg.mode" type="radio" value="round_robin" />
            <span class="text-sm text-n-slate-12">{{
              t('INBOX_MGMT.INACTIVITY_ASSIGN.MODE_ROUND_ROBIN')
            }}</span>
          </label>
        </div>
      </div>

      <label v-if="cfg.mode === 'agent'" class="block">
        <span class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('INBOX_MGMT.INACTIVITY_ASSIGN.AGENT')
        }}</span>
        <select
          v-model="cfg.agent_id"
          class="w-full max-w-sm px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option :value="null" disabled>
            {{ t('INBOX_MGMT.INACTIVITY_ASSIGN.AGENT_PLACEHOLDER') }}
          </option>
          <option v-for="a in agents" :key="a.id" :value="a.id">
            {{ a.name }}
          </option>
        </select>
        <span
          v-if="!agents.length"
          class="block mt-1 text-[0.6875rem] text-n-ruby-11"
          >{{ t('INBOX_MGMT.INACTIVITY_ASSIGN.NO_AGENTS') }}</span
        >
      </label>

      <label class="flex items-center gap-2 cursor-pointer">
        <input v-model="cfg.only_unassigned" type="checkbox" />
        <span class="text-sm text-n-slate-12">{{
          t('INBOX_MGMT.INACTIVITY_ASSIGN.ONLY_UNASSIGNED')
        }}</span>
      </label>
    </div>

    <div class="flex justify-end mt-4">
      <button
        class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-n-brand hover:bg-n-brand/90 disabled:opacity-50"
        :disabled="saving"
        @click="save"
      >
        {{
          saving
            ? t('INBOX_MGMT.INACTIVITY_ASSIGN.SAVING')
            : t('INBOX_MGMT.INACTIVITY_ASSIGN.SAVE')
        }}
      </button>
    </div>
  </div>
</template>
